import React, { useState } from "react";
import { Layout } from "../Layouts/Layout";
import {
  Campaign as CampaignType,
  GroupCategory,
  Team,
  Assignment,
} from "../types";
import { CampaignEdit } from "../Components/Campaign/Edit";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_CAMPAIGN,
  FormsResult,
  GET_FORMS,
  SectionsResult,
  GET_SECTIONS,
  UPDATE_CAMPAIGN,
  UPDATE_CAMPAIGN_ASSIGNMENT,
  UPDATE_ASSIGNMENT_POINTS,
  CampaignResult,
  UsersResult,
  GET_USERS,
  ResponsesResult,
  SEND_CAMPAIGN_REMINDERS,
  EXCLUDE_GROUP,
  GET_RESPONSES_SUMMARY,
  GroupCategoriesResult,
  GET_GROUP_CATEGORIES,
  SET_CAMPAIGN_GROUPCATEGORY,
  DELETE_CAMPAIGN,
  PUSH_CAMPAIGN_GRADES,
} from "../Queries";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { BlockingLoader } from "../Components/BlockingLoader";
import {
  CampaignState as CampaignStateType,
  CampaignType as CampaignTypeEnum,
} from "../types";

export const EditCampaign = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");

  //
  // Queries
  //

  const { loading: campaignLoading, data: campaignData } =
    useQuery<CampaignResult>(GET_CAMPAIGN, {
      variables: { pk: campaignId },
      onError: (error) => setError(t("errors.campaigns.get"), error),
    });

  const { loading: sectionsLoading, data: sectionsData } =
    useQuery<SectionsResult>(GET_SECTIONS, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.sections.list"), error),
    });

  const { loading: formsLoading, data: formsData } = useQuery<FormsResult>(
    GET_FORMS,
    {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.forms.list"), error),
    },
  );

  const { loading: categoriesLoading, data: categoriesData } =
    useQuery<GroupCategoriesResult>(GET_GROUP_CATEGORIES, {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.groups.categories.list"), error),
    });

  const { loading: responsesLoading, data: responsesData } =
    useQuery<ResponsesResult>(GET_RESPONSES_SUMMARY, {
      variables: {
        filters: {
          campaignId,
        },
      },
      onError: (error) => setError(t("errors.responses.getSummary"), error),
    });

  //
  // Mutations
  //

  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN, {
    refetchQueries: ["GetResponsesSummary"],
    onError: (error) => setError(t("errors.campaigns.update"), error),
  });

  const [updateCampaignAssignment] = useMutation(UPDATE_CAMPAIGN_ASSIGNMENT, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.campaigns.updateAssignment"), error),
  });

  const [updateAssignmentPoints] = useMutation(UPDATE_ASSIGNMENT_POINTS, {
    onError: (error) =>
      setError(t("errors.campaigns.updateAssignmentPoints"), error),
  });

  const [sendCampaignReminders] = useMutation(SEND_CAMPAIGN_REMINDERS, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.campaigns.sendReminder"), error),
  });

  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, {
    refetchQueries: ["GetCampaigns"],
    onError: (error) => setError(t("errors.campaigns.delete"), error),
  });

  const [excludeTeam] = useMutation(EXCLUDE_GROUP, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.groups.exclude"), error),
  });

  const [setCampaignGroupCategory] = useMutation(SET_CAMPAIGN_GROUPCATEGORY, {
    refetchQueries: ["GetCampaign"],
    onError: (error) => setError(t("errors.campaigns.setGroupCategory"), error),
  });

  const [pushCampaignGrades, { loading: isPushingGrades }] = useMutation(
    PUSH_CAMPAIGN_GRADES,
    {
      refetchQueries: ["GetCampaign"],
      onError: (error) => setError(t("errors.campaigns.pushGrades"), error),
    },
  );

  // TODO: SHIFT THIS FILTERING TO BACKEND!!!!
  const templates = formsData
    ? formsData.forms.filter(
        (form) =>
          form.isMine || form.isDirectShare || form.isShared || form.isGlobal,
      )
    : [];

  const categories = categoriesData ? categoriesData.groupCategories : [];
  const sections = sectionsData ? sectionsData.sections : [];
  const campaign = campaignData ? campaignData.campaign : undefined;
  const section =
    campaign && sections
      ? sections.find((target) => target.id === campaign?.sectionId)
      : undefined;
  // const template =
  //   campaign && templates
  //     ? templates.find((target) => target.id === campaign?.formId)
  //     : undefined;
  const template = campaign && campaign.form ? campaign.form : undefined;

  // TODO: Refactor and relocate with other queries
  const { loading: usersLoading, data: usersData } = useQuery<UsersResult>(
    GET_USERS,
    {
      variables: {
        filters: {
          sections: campaign
            ? campaign.sectionId
              ? campaign.sectionId
              : undefined
            : undefined,
        },
      },
      onError: (error) => setError(t("errors.users.list"), error),
    },
  );

  const users = usersData ? usersData.users : [];
  const teams = campaign && campaign.groups ? campaign.groups : [];
  const responses = responsesData ? responsesData.responses : [];
  const showLoading =
    campaignLoading ||
    sectionsLoading ||
    formsLoading ||
    usersLoading ||
    responsesLoading ||
    categoriesLoading;

  if (!campaign) {
    const missingAssets = [];
    if (!campaign) {
      missingAssets.push("Survey");
    }
    // if (!template) {
    //   missingAssets.push("Template");
    // }
    return (
      <Layout
        breadcrumbs={[{ title: t("surveys"), link: "/" }]}
        loading={showLoading}
      >
        {showLoading ? <></> : <>{missingAssets.join(", ")} not found</>}
      </Layout>
    );
  }

  //
  // Event Handlers
  //

  async function handleUpdateAssignmentPoints(
    id: string,
    assignmentPoints: string,
  ) {
    if (!campaign) {
      return;
    }
    await updateAssignmentPoints({
      variables: {
        id,
        assignmentPoints,
      },
      context: {
        debounceKey: `campaign-${campaignId}`,
      },
      optimisticResponse: {
        updateAssignmentPoints: {
          id: campaign.id,
          __typename: "Campaign",
          assignmentEnabled: campaign.assignmentEnabled,
          assignmentPoints: assignmentPoints,
          activity: campaign.activity,
          dueDate: campaign.dueDate,
          form: campaign.form,
          formId: campaign.formId,
          groups: campaign.groups,
          loopInstructor: campaign.loopInstructor,
          loopStudent: campaign.loopStudent,
          name: campaign.name,
          released: campaign.released,
          sectionId: campaign.sectionId,
          state: campaign.state,
          submittedResponses: campaign.submittedResponses,
          totalResponses: campaign.totalResponses,
        },
      },
    });
  }

  async function handleAssignmentChange(
    changedCampaign: CampaignType,
    activityLabel: string,
  ) {
    await updateCampaignAssignment({
      variables: {
        id: changedCampaign.id,
        assignmentEnabled: changedCampaign.assignmentEnabled,
        assignmentGradingMode: changedCampaign.assignmentGradingMode,
        assignmentPoints: changedCampaign.assignmentPoints,
        assignmentRatingProportion: changedCampaign.assignmentRatingProportion,
      },
      optimisticResponse: {
        updateCampaignAssignment: {
          id: changedCampaign.id,
          __typename: "Campaign",
          assignmentEnabled: changedCampaign.assignmentEnabled,
          assignmentGradingMode: changedCampaign.assignmentGradingMode,
          assignmentPoints: changedCampaign.assignmentPoints,
          assignmentRatingProportion:
            changedCampaign.assignmentRatingProportion,
          activity: changedCampaign.activity,
          dueDate: changedCampaign.dueDate,
          form: changedCampaign.form,
          formId: changedCampaign.formId,
          gradesSyncedToLms: changedCampaign.gradesSyncedToLms,
          groups: changedCampaign.groups,
          groupcategory: changedCampaign.groupcategory,
          loopInstructor: changedCampaign.loopInstructor,
          loopStudent: changedCampaign.loopStudent,
          name: changedCampaign.name,
          released: changedCampaign.released,
          sectionId: changedCampaign.sectionId,
          state: changedCampaign.state,
          submittedResponses: changedCampaign.submittedResponses,
          totalResponses: changedCampaign.totalResponses,
        },
      },
    });
  }

  async function handleCampaignChange(
    changedCampaign: CampaignType,
    activityLabel: string,
  ) {
    // Show a blocking wait state if we're transitioning survey state to schedule/send
    try {
      if (
        campaign &&
        campaign.campaignType === CampaignTypeEnum.Survey &&
        campaign.state !== changedCampaign.state
      ) {
        if (changedCampaign.state === CampaignStateType.Scheduled) {
          setTransitionMessage(t("surveys.wait.scheduling") as string);
          setIsTransitioning(true);
        } else if (
          changedCampaign.state === CampaignStateType.Active ||
          changedCampaign.state === CampaignStateType.AssignmentActive
        ) {
          setTransitionMessage(t("surveys.wait.sending") as string);
          setIsTransitioning(true);
        }
      }

      await updateCampaign({
        variables: {
          ...changedCampaign,
          activityLabel,
          // Extract assignment fields if present
          assignmentDescription: changedCampaign.assignment?.description,
          assignmentSendDate: changedCampaign.assignment?.assignmentSendDate,
          assignmentDueDate: changedCampaign.assignment?.assignmentDueDate,
          assignmentCloseDate: changedCampaign.assignment?.assignmentCloseDate,
          submissionType: changedCampaign.assignment?.submissionType,
        },
        context: {
          debounceKey: `campaign-${changedCampaign.id}`,
        },
        optimisticResponse: {
          updateCampaign: {
            id: changedCampaign.id,
            __typename: "Campaign",
            assignment: changedCampaign.assignment,
            assignmentEnabled: changedCampaign.assignmentEnabled,
            assignmentPoints: changedCampaign.assignmentPoints,
            assignmentGradingMode: changedCampaign.assignmentGradingMode,
            assignmentRatingProportion:
              changedCampaign.assignmentRatingProportion,
            autoSendDate: changedCampaign.autoSendDate,
            autoCloseDate: changedCampaign.autoCloseDate,
            autoReleaseDate: changedCampaign.autoReleaseDate,
            activity: changedCampaign.activity,
            campaignType: changedCampaign.campaignType,
            dueDate: changedCampaign.dueDate,
            form: changedCampaign.form,
            formId: changedCampaign.formId,
            gradesSyncedToLms: changedCampaign.gradesSyncedToLms,
            groups: changedCampaign.groups,
            groupcategory: changedCampaign.groupcategory,
            loopInstructor: changedCampaign.loopInstructor,
            loopStudent: changedCampaign.loopStudent,
            name: changedCampaign.name,
            released: changedCampaign.released,
            sectionId: changedCampaign.sectionId,
            state: changedCampaign.state,
            submittedResponses: changedCampaign.submittedResponses,
            totalResponses: changedCampaign.totalResponses,
          },
        },
      });
    } finally {
      setIsTransitioning(false);
      setTransitionMessage("");
    }
  }

  async function handleDelete(campaign: CampaignType) {
    if (
      window.confirm(
        t("assignments.confirmDelete", { name: campaign.name }) as string,
      )
    ) {
      await deleteCampaign({
        variables: {
          id: campaign.id,
        },
      });
      navigate("/");
    }
  }

  async function handleRemind(campaign: CampaignType) {
    const sent = await sendCampaignReminders({
      variables: {
        id: campaign.id,
      },
      context: {
        debounceKey: `campaign-${campaign.id}`,
      },
    });

    if (sent) {
      alert("Reminders sent successfully");
    } else {
      alert("No reminders to send");
    }
  }

  async function handleExclude(campaign: CampaignType, team: Team) {
    await excludeTeam({
      variables: {
        campaignId: campaign.id,
        groupId: team.id,
      },
      context: {
        debounceKey: `exclude-${campaign.id}-${team.id}`,
      },
    });
  }

  async function handleSetGroupCategory(
    campaign: CampaignType,
    groupcategory: GroupCategory | undefined,
  ) {
    await setCampaignGroupCategory({
      variables: {
        id: campaign.id,
        groupcategoryId: groupcategory?.id,
      },
      context: {
        debounceKey: `campaign-${campaignId}`,
      },
    });
  }

  async function handlePushGrades() {
    if (!campaign) {
      return;
    }
    try {
      await pushCampaignGrades({
        variables: {
          id: campaign.id,
        },
      });
    } catch (error: any) {
      alert(`Failed to push grades: ${error.message}`);
    }
  }

  return (
    <Layout
      breadcrumbs={[
        { title: t("breadcrumbs.campaigns"), link: "/surveys" },
        { title: campaign.name },
      ]}
      loading={showLoading}
    >
      <BlockingLoader open={isTransitioning} message={transitionMessage} />
      <CampaignEdit
        campaign={campaign}
        categories={categories}
        responses={responses}
        section={section}
        sections={sections}
        teams={teams}
        template={template}
        templates={templates}
        users={users}
        onChange={handleCampaignChange}
        onAssignmentChange={handleAssignmentChange}
        onUpdateAssignmentPoints={handleUpdateAssignmentPoints}
        onRemind={handleRemind}
        onDelete={handleDelete}
        onExclude={handleExclude}
        onSetGroupCategory={handleSetGroupCategory}
        onPushGrades={handlePushGrades}
        isPushingGrades={isPushingGrades}
      />
    </Layout>
  );
};
